import { useEffect, useRef } from "react";
import mixpanel from "mixpanel-browser";
import { useIdentity } from "../../../provider-portal/IdentityProvider";

const CLIENT_EVENT_PROPERTIES = {
  User: "Client",
};

const useFireClientPortalMixpanelEventOnMount = (
  eventName: string,
  eventProperties: Record<string, any>,
) => {
  const { isProvider } = useIdentity();
  const hasFired = useRef(false);
  const env = process.env.NODE_ENV;
  const isClientApp = process.env.REACT_APP_FAY_APP === "client";

  useEffect(() => {
    if (isClientApp && !isProvider && !hasFired.current) {
      hasFired.current = true;
      if (env === "development") {
        console.info(
          `Would have fired mixpanel event for user ${mixpanel.get_distinct_id()} on mount: ${eventName}`,
          {
            ...CLIENT_EVENT_PROPERTIES,
            ...eventProperties,
          },
        );
      } else {
        try {
          mixpanel.track(eventName, {
            ...CLIENT_EVENT_PROPERTIES,
            ...eventProperties,
          });
        } catch (e) {
          console.error("Error firing mixpanel event on mount: ", e);
        }
      }
    }
  }, [isClientApp, isProvider, eventName, eventProperties, env]);
};

const fireClientPortalMixpanelEvent = (
  eventName: string,
  eventProperties: Record<string, any>,
) => {
  const isClientApp = process.env.REACT_APP_FAY_APP === "client";
  const env = process.env.NODE_ENV;

  if (!isClientApp) {
    return;
  }

  if (env === "development") {
    console.info(
      `Would have fired mixpanel event for user ${mixpanel.get_distinct_id()}: ${eventName}`,
      {
        ...CLIENT_EVENT_PROPERTIES,
        ...eventProperties,
      },
    );
  } else {
    try {
      mixpanel.track(eventName, {
        ...CLIENT_EVENT_PROPERTIES,
        ...eventProperties,
      });
    } catch (e) {
      console.error("Error firing mixpanel event: ", e);
    }
  }
};

const mixpanelIdentifyClient = (userId: string) => {
  mixpanel.identify(userId);
  mixpanel.people.set_once({
    Role: "Client",
  });
};

const mixpanelResetIdentity = () => {
  if (process.env.NODE_ENV === "development") {
    console.info(
      `Resetting current mixpanel identity: ${mixpanel.get_distinct_id()}`,
    );
  }
  mixpanel.reset();
};

export {
  useFireClientPortalMixpanelEventOnMount,
  fireClientPortalMixpanelEvent,
  mixpanelIdentifyClient,
  mixpanelResetIdentity,
};
